import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canViewFinanceDashboard,
  comparisonRange,
  defaultCustomLayout,
  formatDateRange,
  matchingRangeKey,
  rangeDays,
  type CustomDashboardItem,
} from '@promanage/core';
import { Button, Panel } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { Icon } from '../components/Icon';
import { DashboardControls } from '../features/dashboard/DashboardControls';
import { DashboardLayoutMenu } from '../features/dashboard/DashboardLayoutMenu';
import { ShortcutsLink } from '../features/shortcuts/ShortcutKeys';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { CustomGrid } from '../features/dashboard/CustomGrid';
import { WidgetPicker } from '../features/dashboard/WidgetPicker';
import { addWidget, neededSources, usesPeriod } from '../features/dashboard/customLayout';
import { dashboardSearch, resolveRange, type DashboardSearch } from '../features/dashboard/period';
import { portfolioScopeLabel } from '../features/portfolio/PortfolioScopeOptions';
import { portfolioScopeFor, usePortfolioScope } from '../features/portfolio/scope';
import { resolveSession, useSession } from '../features/auth/session';
import { todayIso } from '../features/property/today';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/dashboard/custom')({
  // The same period as the other two overviews, parsed by the same function — see the note on `/`.
  validateSearch: (search: Record<string, unknown>): DashboardSearch => dashboardSearch(search),
  loaderDeps: ({ search }) => dashboardSearch(search),
  /*
   * THE LAYOUT FIRST, THEN ONLY WHAT IT NEEDS. Which summaries to fetch is a question about the
   * widgets on the page, so the loader reads the arrangement and prefetches just those — a property
   * manager's page never asks for the trust summary, and a page of finance cards never pays for the
   * portfolio pass.
   */
  loader: async ({ context, deps }) => {
    const session = await resolveSession(context.queryClient);
    const custom = await context.queryClient.ensureQueryData(
      trpc.dashboard.customLayout.queryOptions(undefined, { staleTime: 5 * 60_000 }),
    );
    const needs = neededSources(custom.layout.items);

    await Promise.all([
      needs.finance && session !== null && canViewFinanceDashboard(session.roles)
        ? context.queryClient.prefetchQuery(
            trpc.dashboard.summary.queryOptions(deps, { staleTime: 30_000 }),
          )
        : null,
      needs.management
        ? context.queryClient.prefetchQuery(
            trpc.dashboard.management.queryOptions(
              { ...deps, scope: portfolioScopeFor(session) },
              { staleTime: 60_000 },
            ),
          )
        : null,
    ]);
  },
  component: CustomDashboard,
});

/**
 * The custom overview: the person's own arrangement of widgets from both of the others.
 *
 * READING IT is the same as reading the other two — the same bar, the same period, the same cards.
 * ARRANGING IT happens in place: "Edit" turns each card into a picture with a bar above it, and the
 * changes are a DRAFT until "Done" saves them. "Cancel" throws the draft away, so an experiment
 * cannot cost somebody the page they had.
 *
 * WHAT MAY BE PLACED is decided per widget in core (`availableWidgets`) and again by the server when
 * the layout is stored and read back. The figures themselves still come through `dashboard.summary`
 * and `dashboard.management` with their own gates — this page holds an arrangement, never a
 * permission.
 */
function CustomDashboard() {
  const session = useSession();
  const roles = session?.roles ?? [];
  const { scope } = usePortfolioScope();
  const navigate = useNavigate({ from: Route.fullPath });
  const client = useQueryClient();
  const params = dashboardSearch(Route.useSearch());
  const today = todayIso();

  const layout = useQuery(
    trpc.dashboard.customLayout.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load your dashboard' },
      staleTime: 5 * 60_000,
    }),
  );

  /** The arrangement being edited, or null when not editing. */
  const [draft, setDraft] = useState<CustomDashboardItem[] | null>(null);
  const [picking, setPicking] = useState(false);
  const editing = draft !== null;
  /*
   * An arrangement being edited is unsaved work, and leaving dropped it without a word — by the
   * rail, and since `g` works on every page, by two keystrokes. The page used to switch its keys
   * off while editing to cover the second; the guard covers both, as it does for every form.
   */
  useUnsavedChanges(editing);
  const items = draft ?? layout.data?.layout.items ?? [];

  const needs = neededSources(items);
  const mayFinance = canViewFinanceDashboard(roles);

  const finance = useQuery(
    trpc.dashboard.summary.queryOptions(params, {
      meta: { errorTitle: 'Could not load the trust figures' },
      staleTime: 30_000,
      placeholderData: keepPreviousData,
      // Never asked for by somebody the server would refuse, whatever a layout says.
      enabled: needs.finance && mayFinance,
    }),
  );
  const management = useQuery(
    trpc.dashboard.management.queryOptions(
      { ...params, scope },
      {
        meta: { errorTitle: 'Could not load the property figures' },
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled: needs.management,
      },
    ),
  );

  // The server's clamped answer wins over what the URL asked for — the other overviews' rule.
  const asked = resolveRange(params, today);
  const range = finance.data?.range ?? management.data?.range ?? asked;
  const comparison =
    finance.data?.comparison ?? management.data?.comparison ?? comparisonRange(range);
  const managementRange = management.data?.range ?? asked;

  const save = useMutation(
    trpc.dashboard.saveCustomLayout.mutationOptions({
      meta: { errorTitle: 'Could not save your dashboard' },
      onSuccess: (saved) => {
        // What the server KEPT, which is what is shown — it may have removed a widget.
        client.setQueryData(trpc.dashboard.customLayout.queryKey(), saved);
        setDraft(null);
      },
    }),
  );

  const startEditing = () => setDraft([...(layout.data?.layout.items ?? [])]);

  const chooseRange = (next: DashboardSearch) => {
    void navigate({ search: next, replace: true });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Overview"
        // Only when a property card is on the page: its totals are this portfolio's, not the agency's.
        {...(needs.management && management.data
          ? { subtitle: portfolioScopeLabel(management.data.scope) }
          : {})}
        actions={
          editing ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
                <Icon name="plus" size={14} />
                Add widget
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDraft(defaultCustomLayout(roles).items)}
              >
                Reset
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={save.isPending}
                onClick={() => save.mutate({ layout: { version: 1, items } })}
              >
                {save.isPending ? 'Saving' : 'Done'}
              </Button>
            </>
          ) : (
            <>
              <DashboardLayoutMenu current="custom" search={params} />
              {/* The period picker only when something on the page moves with it. */}
              {usesPeriod(items) ? (
                <DashboardControls
                  range={range}
                  comparison={comparison}
                  activeKey={matchingRangeKey(range, today)}
                  onChange={chooseRange}
                />
              ) : null}
              <Button size="sm" variant="secondary" onClick={startEditing} disabled={!layout.data}>
                <Icon name="columns" size={14} />
                Edit
              </Button>
            </>
          )
        }
      />

      <div className="flex flex-col gap-4">
        {layout.error || finance.error || management.error ? (
          <LoadFailed
            inline
            retry={() => {
              void layout.refetch();
              if (needs.finance && mayFinance) void finance.refetch();
              if (needs.management) void management.refetch();
            }}
            retrying={layout.isFetching || finance.isFetching || management.isFetching}
          />
        ) : null}

        {editing ? (
          <p className="text-body-sm text-ink-muted">
            Drag a card onto another to swap places, or use its arrows. Changes are saved when you
            press Done.
          </p>
        ) : null}

        {layout.data && items.length === 0 ? (
          <Panel className="flex flex-col items-start gap-2 p-5">
            <h2 className="text-heading font-bold tracking-[-0.014em]">
              Nothing on your dashboard
            </h2>
            <p className="text-body-sm text-ink-muted">
              Add the cards you check most, and arrange them how you work.
            </p>
            <Button
              size="sm"
              className="mt-1"
              onClick={() => {
                if (!editing) startEditing();
                setPicking(true);
              }}
            >
              <Icon name="plus" size={14} />
              Add widget
            </Button>
          </Panel>
        ) : (
          <CustomGrid
            items={items}
            editing={editing}
            onChange={setDraft}
            data={{
              finance: finance.data,
              management: management.data,
              today,
              roles,
              mayClosePeriods: session?.mayClosePeriods ?? false,
              financeRangeLabel: formatDateRange(finance.data?.range ?? asked),
              managementRangeLabel: formatDateRange(managementRange),
              managementRangeDays: rangeDays(managementRange),
            }}
          />
        )}

        <ShortcutsLink />
      </div>

      <WidgetPicker
        open={picking}
        onOpenChange={setPicking}
        roles={roles}
        placed={items}
        onAdd={(widget) => setDraft((current) => addWidget(current ?? items, widget))}
      />
    </div>
  );
}
